import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams,useLocation  } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios'; 

// Context & API
import { useAuth } from '../../../context/AuthContext';
import { saveOrUpdateUserProfileApi, fetchUserJogetProfileApi,fetchUserJogetProfileUUDIApi } from '../../../services/api';
import { fetchLookupDataApi } from '../../../services/lookupApi';

import MetaTags from '../../../components/MetaTags';

// Components
import ProfileWizard from '../../../components/dashboard/profile/ProfileWizard';
import StepPersonalInfo from '../../../components/dashboard/profile/StepPersonalInfo';
import StepPhysicalStats from '../../../components/dashboard/profile/StepPhysicalStats';
import StepAddress from '../../../components/dashboard/profile/StepAddress';
import StepSocialLinks from '../../../components/dashboard/profile/StepSocialLinks';

// Utils & Schema
import { profileSchema } from '../../../validation/profileSchema';
import { apiToDateObject, dateObjectToApiFormat } from '../../../utils/dateHelpers';
import { convertCmToFeetInches, convertFeetInchesToCm, convertCmToMeters } from '../../../utils/heightHelpers';
import { transformToSelectOptions } from '../../../utils/selectUtils';
import Swal from 'sweetalert2';

const MyProfilePage = () => {
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [currentJogetRecordId, setCurrentJogetRecordId] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [isWhatsappSame, setIsWhatsappSame] = useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const isViewMode = query.get("mode") === "view";

    const profileId = params.id;
    const hasAdminToken = !!localStorage.getItem('adminToken');

    const [lookupOptions, setLookupOptions] = useState({
        bodyType: [], gender: [], specialCategory: [], nationality: [], countryOfResidence: [], preferredCurrency: [],
        eyeColour: [], skinType: [], hairType: [], hairColor: [], talent: [], addressType: [], educationLevel: [], sportsType:[], esportsType:[], drivingType:[],
        peopleAppearanceList: [], 
    });

    const { control, handleSubmit, reset, trigger, setValue, watch, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(profileSchema),
        mode: 'onChange',
        defaultValues: { 
            dateOfBirth: null, addressType: 'primary', heightFeet: null, heightInches: null, talents: [], imdbUrl: '' 
        } 
    });

    // --- State & Watchers ---
    const isSports = selectedSkills.some(v => v.value === "sports");
    const isEsports = selectedSkills.some(v => v.value === "esports");
    const mergedOptions = [
        ...(Array.isArray(lookupOptions.sportsType) ? lookupOptions.sportsType : []),
        ...(Array.isArray(lookupOptions.esportsType) ? lookupOptions.esportsType : [])
    ].filter((v, i, arr) => arr.findIndex(t => t.value === v.value) === i);
    
    const [firstFilled, setFirstFilled] = useState(null);
    const pan = watch("panNumber");
    const aadhar = watch("aadharNumber");
    const watchedMobile = watch('mobileNumber');
    const watchedHeightFeet = watch('heightFeet');
    const watchedHeightInches = watch('heightInches');
    const watchedPincode = watch('pinCode');

    useEffect(() => { if (pan || aadhar) clearErrors(["panNumber", "aadharNumber"]); }, [pan, aadhar, clearErrors]);
    useEffect(() => {
        if (!firstFilled) {
            if (pan) setFirstFilled("pan");
            else if (aadhar) setFirstFilled("aadhar");
        }
    }, [pan, aadhar, firstFilled]);
    useEffect(() => { if (!pan && !aadhar) setFirstFilled(null); }, [pan, aadhar]);

    const isPanRequired = (!pan && !aadhar) || firstFilled === "pan";
    const isAadharRequired = (!pan && !aadhar) || firstFilled === "aadhar";

    const feetOptions = Array.from({ length: 8 }, (_, i) => ({ value: i + 1, label: `${i + 1} Ft` }));
    const inchOptions = Array.from({ length: 12 }, (_, i) => ({ value: i, label: `${i} In` }));

    const displayCalculatedHeight = useMemo(() => {
        if (watchedHeightFeet !== null && watchedHeightInches !== null) {
            const totalCm = convertFeetInchesToCm(watchedHeightFeet, watchedHeightInches);
            const totalMeters = convertCmToMeters(totalCm);
            return totalCm ? `${watchedHeightFeet}' ${watchedHeightInches}" (${totalMeters} m)` : '';
        }
        return '';
    }, [watchedHeightFeet, watchedHeightInches]);

    useEffect(() => { if (isWhatsappSame) setValue('whatsappNo', watchedMobile || ''); }, [isWhatsappSame, watchedMobile, setValue]);

    const handleWhatsappCheck = (e) => {
        const checked = e.target.checked;
        setIsWhatsappSame(checked);
        if (checked) setValue('whatsappNo', watchedMobile || '');
        else setValue('whatsappNo', '');
    };

    const handlePincodeLookup = useCallback(async (pincode) => {
        if (!pincode || pincode.length !== 6) return;
        try {
            // const response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
            let response;
            try {
                // 🔥 FIRST API
                response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);

                // 🔥 IF FAILED THEN BACKUP API
                if (!response.data?.[0] || response.data?.[0]?.Status !== "Success") {
                    response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                    
                    // 🔥 BACKUP RESPONSE FORMAT FIX
                    response.data = [{
                        Status: response.data.Status,
                        PostOffice: response.data.PostOffice
                    }];
                }
            } catch {
                // 🔥 DIRECT BACKUP API
                response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                
                response.data = [{
                    Status: response.data.Status,
                    PostOffice: response.data.PostOffice
                }];
            }
            if (response.data?.[0]?.Status === 'Success') {
                const { State, District } = response.data[0].PostOffice[0];
                setValue('state', State, { shouldValidate: true });
                setValue('district', District, { shouldValidate: true });
            } else {
                setValue('state', ''); setValue('district', '');
                setApiMessage({ type: 'warning', text: `Pincode not found.` });
            }
        } catch (error) { 
            setApiMessage({ type: 'danger', text: 'Failed to fetch pincode details.' });
        }
    }, [setValue]);

    useEffect(() => { if (watchedPincode && watchedPincode.length === 6) handlePincodeLookup(watchedPincode); }, [watchedPincode, handlePincodeLookup]);

    const WIZARD_STEPS = [
        { id: 0, title: 'Personal Info', fields: ['fullName', 'email', 'mobileNumber', 'dateOfBirth', 'gender','specialCategory', 'countryOfResidence', 'preferredCurrency', 'talents', 'alternateMobileNumber', 'educationLevel', 'nationality', 'aboutYourself', 'panNumber', 'aadharNumber'] },
        { id: 1, title: 'Physical Stats', fields: ['weight', 'heightFeet', 'heightInches', 'eyeColour', 'skinType', 'bodyType', 'hairType', 'hairColor','esportsType','drivingType','peopleAppearanceList','sportsType','esportsType'] },
        { id: 2, title: 'Address', fields: ['pinCode', 'addressLine1', 'addressLine2', 'state', 'district'] },
        { id: 3, title: 'Social Links', fields: ['facebookProfileUrl', 'instagramProfileUrl', 'linkedinProfileUrl', 'twitterProfileUrl', 'youtubeUrl', 'whatsappNo', 'imdbUrl'] }
    ];

    const loadData = useCallback(async () => {
        // if (!user?.id) return;
        if (!user?.id && !hasAdminToken) return;
        // if (!user?.id && !hasAdminToken && !profileId) return;
        setPageLoading(true);
        try {
            const lookupGroups = {
                bodyType: 'bodyType', gender: 'gender', specialCategory:'specialCategory', nationality: 'nationality',
                countryOfResidence: 'countryOfResidence', preferredCurrency: 'preferredCurrency', eyeColour: 'eyeColour',
                skinType: 'skinColor', hairType: 'hairType', hairColor: 'hairColor', talent: 'jobTalent', addressType: 'addressType', 
                educationLevel: 'eduQualification', esportsType: 'esportsType', sportsType: 'sportsType', drivingType: 'drivingType', peopleAppearanceList: 'peopleAppearanceList'
            };
            
            const promises = Object.entries(lookupGroups).map(([key, group]) => fetchLookupDataApi(group));
            const results = await Promise.all(promises);
            
            let finalLookups = {};
            Object.keys(lookupGroups).forEach((key, i) => { finalLookups[key] = transformToSelectOptions(results[i].data.data); });
            setLookupOptions(finalLookups);
            
            // const response = await fetchUserJogetProfileApi(user.id);

            let jogetData = null;

// ADMIN EDIT
if (hasAdminToken && profileId && profileId !== "add") {

  const response = await fetchUserJogetProfileApi(profileId);
  jogetData = response?.data?.jogetData;
}

// OPEN OTHER USER PROFILE USING UUID
else if (!hasAdminToken && profileId) {

  const response = await fetchUserJogetProfileUUDIApi(profileId);
  jogetData = response?.data?.jogetData;
  setProfileImagePreview(
    response?.data?.profileImageUrl ||
    jogetData?.profileImage ||
    null
  );
}

// OWN PROFILE
else if (!hasAdminToken && user?.id) {

  const response = await fetchUserJogetProfileApi(user.id);
  jogetData = response?.data?.jogetData;
}
//             let response = null;

// if (hasAdminToken && profileId !== "add") {
//   response = await fetchUserJogetProfileApi(profileId);
// }
// else if (!hasAdminToken && user?.id) {
//   response = await fetchUserJogetProfileApi(user.id);
// }

//             // const jogetData = response.data.jogetData;
//             const jogetData = response?.data?.jogetData;


            if (jogetData) {
                setCurrentJogetRecordId(jogetData.pk_id);

                const talentsForSelect = (jogetData.talent || '').split(',')
                    .map(val => val.trim()).map(val => finalLookups.talent.find(opt => String(opt.value) === String(val))).filter(Boolean);

                let heightFeet = null, heightInches = null;
                if (jogetData.height) {
                    const h = convertCmToFeetInches(parseFloat(jogetData.height));
                    heightFeet = h.feet; heightInches = h.inches;
                }

                // reset({
                //     ...jogetData,
                //     talents: talentsForSelect, dateOfBirth: apiToDateObject(jogetData.dateOfBirth), 
                //     heightFeet, heightInches, eyeColour: jogetData.eyeColour || '', skinType: jogetData.skinType || '',
                //     bodyType: jogetData.bodyType || '', hairType: jogetData.hairType || '', hairColor: jogetData.hairColor || '',
                //     drivingType: jogetData.drivingType || '', peopleAppearanceList: jogetData.peopleAppearanceList || '',
                //     esportsType: Array.isArray(jogetData.esportsType) ? jogetData.esportsType.map(val => finalLookups.esportsType.find(opt => opt.value === val)).filter(Boolean) : [],
                //     sportsType: Array.isArray(jogetData.sportsType) ? jogetData.sportsType.map(val => finalLookups.sportsType.find(opt => opt.value === val)).filter(Boolean) : [],
                //     imdbUrl: jogetData.imdbUrl || '',addressType: 'Primary', 
                // });
                reset({
                  ...jogetData,

                  // 🔥 BASIC
                  addressType: jogetData?.addressType || 'primary',
                  dateOfBirth: apiToDateObject(jogetData.dateOfBirth),

                  // 🔥 TALENTS (multi)
                  talents: talentsForSelect,

                  // 🔥 HEIGHT
                  heightFeet,
                  heightInches,

                  // 🔥 SELECT FIELDS (IMPORTANT FIX 🔥)
                  educationLevel: finalLookups.educationLevel.find(
                    opt => opt.value === jogetData.educationLevel
                  ) || null,

                  gender: finalLookups.gender.find(
                    opt => opt.value === jogetData.gender
                  ) || null,

                  nationality: finalLookups.nationality.find(
                    opt => opt.value === jogetData.nationality
                  ) || null,

                  countryOfResidence: finalLookups.countryOfResidence.find(
                    opt => opt.value === jogetData.countryOfResidence
                  ) || null,

                  preferredCurrency: finalLookups.preferredCurrency.find(
                    opt => opt.value === jogetData.preferredCurrency
                  ) || null,

                  eyeColour: finalLookups.eyeColour.find(
                    opt => opt.value === jogetData.eyeColour
                  ) || null,

                  skinType: finalLookups.skinType.find(
                    opt => opt.value === jogetData.skinType
                  ) || null,

                  bodyType: finalLookups.bodyType.find(
                    opt => opt.value === jogetData.bodyType
                  ) || null,

                  hairType: finalLookups.hairType.find(
                    opt => opt.value === jogetData.hairType
                  ) || null,

                  hairColor: finalLookups.hairColor.find(
                    opt => opt.value === jogetData.hairColor
                  ) || null,

                  drivingType: finalLookups.drivingType.find(
                    opt => opt.value === jogetData.drivingType
                  ) || null,

                  peopleAppearanceList: finalLookups.peopleAppearanceList.find(
                    opt => opt.value === jogetData.peopleAppearanceList
                  ) || null,

                  // 🔥 MULTI SELECT
                  esportsType: Array.isArray(jogetData.esportsType)
                    ? jogetData.esportsType
                        .map(val => finalLookups.esportsType.find(opt => opt.value === val))
                        .filter(Boolean)
                    : [],

                  sportsType: Array.isArray(jogetData.sportsType)
                    ? jogetData.sportsType
                        .map(val => finalLookups.sportsType.find(opt => opt.value === val))
                        .filter(Boolean)
                    : [],

                  // 🔥 NORMAL FIELDS
                  imdbUrl: jogetData.imdbUrl || '',
                });

                if(jogetData.whatsappNo && jogetData.whatsappNo === jogetData.mobileNumber) setIsWhatsappSame(true);
                if (jogetData.profileImage) setProfileImagePreview(jogetData.profileImage || jogetData.profileImageUrl);
            }
        } catch (error) { 
            setApiMessage({ type: 'danger', text: 'Failed to load profile.' }); 
        } finally { setPageLoading(false); }
    // }, [user, reset]);
    }, [user, reset, hasAdminToken, profileId]);

    useEffect(() => { loadData(); }, [loadData]);

    // const handleNext = async () => {
    //     const isValid = await trigger(WIZARD_STEPS[currentStep].fields);
    //     if (isValid) {
    //         setApiMessage({ type: '', text: '' });
    //         if (currentStep < WIZARD_STEPS.length - 1) { setCurrentStep(c => c + 1); window.scrollTo(0,0); }
    //         else handleSubmit(onSubmit)();
    //     } else {
    //         setApiMessage({ type: 'danger', text: 'Please fill all required fields.' }); 
    //         window.scrollTo(0,0);
    //     }
    // };

    const handleNext = async () => {
    console.log("STEP:", currentStep);

    const isValid = await trigger(WIZARD_STEPS[currentStep].fields);

    // 🔥 FULL DEBUG
    console.log("IS VALID:", isValid);

    const allErrors = await trigger(); // 👈 FULL FORM VALIDATION
    console.log("FULL FORM VALID:", allErrors);

    console.log("ERROR OBJECT:", errors);

    if (!isValid) {
      console.log("FAILED FIELDS:");
      WIZARD_STEPS[currentStep].fields.forEach(field => {
        if (errors[field]) {
          console.log(field, "=>", errors[field].message);
        }
      });
      return;
    }

    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(c => c + 1);
    } else {
      handleSubmit(onSubmit)();
    }
  };


  const logErrors = (obj, parent = "") => {
    Object.keys(obj).forEach(key => {
      const path = parent ? `${parent}.${key}` : key;
      if (obj[key]?.message) {
        console.log(path, "=>", obj[key].message);
      }
      if (obj[key] && typeof obj[key] === "object") {
        logErrors(obj[key], path);
      }
    });
  };

  logErrors(errors);


    const handleBack = () => { if (currentStep > 0) setCurrentStep(c => c - 1); window.scrollTo(0,0); };
    
    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const formData = new FormData();
            // formData.append('user_id', user.id);
            if (hasAdminToken && profileId !== "add") {
  formData.append('user_id', profileId);
} else {
  formData.append('user_id', user.id);
}
//             formData.append(
//   'user_id',
//   hasAdminToken ? profileId : user.id
// );
            if (currentJogetRecordId) formData.append('id', currentJogetRecordId);

            const heightInCm = convertFeetInchesToCm(data.heightFeet, data.heightInches);
            formData.append('height', heightInCm ? heightInCm.toFixed(2) : '');
            formData.append('talent', (data.talents || []).map(t => t.value).join(','));

            const exclude = ['heightFeet', 'heightInches', 'talents', 'height', 'talent', 'profileImageFile', 'user_id', 'id', 'pk_id'];

            Object.keys(data).forEach(key => {
            if (!exclude.includes(key) && data[key] !== null && data[key] !== undefined) {

                let value = data[key];

                // ✅ MULTI SELECT
                if (Array.isArray(value)) {
                value = value.map(v => (typeof v === "object" ? v.value : v)).join(',');

                // ✅ SINGLE SELECT
                } else if (typeof value === "object") {
                value = value?.value ?? value;

                // ✅ DATE
                } 
                if (key === 'dateOfBirth' && value instanceof Date) {
                value = dateObjectToApiFormat(value);
                }

                formData.append(key, value);
            }
            });



            if (data.profileImageFile instanceof File) formData.append('profileImageFile', data.profileImageFile);

            await saveOrUpdateUserProfileApi(formData);
            setApiMessage({ type: 'success', text: 'Profile Updated Successfully!' });
                        if (hasAdminToken) {
  setTimeout(() => {
    navigate('/admin/users');
  }, 1000);
}
        } catch (error) { 
            setApiMessage({ type: 'danger', text: 'Update Failed' }); 
        } finally { setLoading(false); }
    };

    // const handleImageChange = (e) => {
    //     if (e.target.files[0]) {
    //         setValue('profileImageFile', e.target.files[0]);
    //         setProfileImagePreview(URL.createObjectURL(e.target.files[0]));
    //     }
    // };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        // ✅ 1MB LIMIT
        const maxSize = 1 * 1024 * 1024;
        // ✅ ONLY IMAGE CHECK
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File',
                text: 'Only image files are allowed.',
                confirmButtonColor: '#e50914'
            });
            e.target.value = '';
            return;
        }
        // ✅ FILE SIZE CHECK
        if (file.size > maxSize) {
            Swal.fire({
                icon: 'warning',
                title: 'Max 1MB Only',
                text: 'Upload image below 1MB',
                confirmButtonColor: '#e50914',
                customClass: {
                    popup: 'custom-swal-popup',
                    title: 'custom-swal-title',
                    htmlContainer: 'custom-swal-text',
                    confirmButton: 'custom-swal-btn',
                    icon: 'custom-swal-icon'
                }
            });
            e.target.value = '';
            return;
        }
        // ✅ VALID IMAGE
        setValue('profileImageFile', file);
        setProfileImagePreview(URL.createObjectURL(file));
    };

    if (pageLoading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;

    return (
        <div className="my-profile-page-wrapper">
            <MetaTags title="My Profile" />
            <h5 className="text-uppercase text-white fw-bold mb-4"><span className="text-danger border-start border-4 border-danger ps-3">My Profile</span></h5>

            <ProfileWizard currentStep={currentStep} steps={WIZARD_STEPS} />

            {apiMessage.text && <Alert variant={apiMessage.type} dismissible onClose={() => setApiMessage({type:'', text:''})}>{apiMessage.text}</Alert>}

            <div className="custom-card-box">
                <Form noValidate>
                    {currentStep === 0 && <StepPersonalInfo control={control} errors={errors} lookupOptions={lookupOptions} profileImagePreview={profileImagePreview} handleImageChange={handleImageChange} isWhatsappSame={isWhatsappSame} handleWhatsappCheck={handleWhatsappCheck} isPanRequired={isPanRequired} isAadharRequired={isAadharRequired} />}
                    {currentStep === 1 && <StepPhysicalStats control={control} errors={errors} lookupOptions={lookupOptions} feetOptions={feetOptions} inchOptions={inchOptions} displayCalculatedHeight={displayCalculatedHeight} selectedSkills={selectedSkills} setSelectedSkills={setSelectedSkills} isSports={isSports} isEsports={isEsports} mergedOptions={mergedOptions} />}
                    {currentStep === 2 && <StepAddress control={control} errors={errors} />}
                    {currentStep === 3 && <StepSocialLinks control={control} errors={errors} isWhatsappSame={isWhatsappSame} />}
                </Form>
            </div>

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={handleBack} disabled={currentStep === 0 || loading} size="lg" className="px-4 border-secondary text-light"><FaChevronLeft className="me-2" /> Back</Button>
                {!(isViewMode && currentStep === 3) && ( <Button variant="danger" onClick={handleNext} disabled={loading} size="lg" className="px-5 fw-bold shadow-lg" style={{ minWidth: '180px' }}>{loading ? <Spinner animation="border" size="sm" className="text-white"/> : (currentStep === 3 ? 'SAVE PROFILE' : <>Next <FaChevronRight className="ms-2" /></>)}</Button> )}
            </div>
        </div>
    );
};

export default MyProfilePage;